import { GameApp } from "./game/GameApp";
import { FOUNDING_SEEDS, type HelperMessage } from "./game/help/FakeFifo";
import {
  apiConfigured,
  getHelpNext,
  getScoreboard,
  postClear,
  postIdentity,
  postMessage,
} from "./net/apiClient";
import { UiController, type Identity } from "./ui/screens";

async function bootstrap(): Promise<void> {
  const stage = document.getElementById("stage");
  const uiRoot = document.getElementById("ui-root");
  if (!stage || !uiRoot) throw new Error("missing #stage or #ui-root");

  const ui = new UiController(uiRoot);

  let identity: Identity = { displayName: "Visitor", tag: "" };
  let playerToken: string | null = null;
  let lastClearTokenId: string | null = null;
  let localBoard: HelperMessage[] = [...FOUNDING_SEEDS];

  const game = new GameApp(stage, {
    onHud: ({ actName, progress, deaths, helpers }) =>
      ui.setHud(actName, progress, deaths, helpers),
    onHelpOffer: () => ui.show("help"),
    onDeathForHelp: () => {
      /* modal via onHelpOffer */
    },
    onHelperMessage: (msg) => ui.showHelperMessage(msg),
    onRescueCinema: (messages) => ui.playRescueCinema(messages),
    onClear: async (result) => {
      ui.setResults(result.elapsed, result.helped, result.deaths);
      ui.show("results");

      if (apiConfigured() && playerToken) {
        try {
          const runId = crypto.randomUUID();
          const res = await postClear(playerToken, {
            runId,
            durationSec: Math.round(result.elapsed),
            helped: result.helped,
          });
          lastClearTokenId = res.clearTokenId;
        } catch {
          lastClearTokenId = null;
          ui.toast("Clear recorded locally only (API unavailable).");
        }
      } else {
        // Local-dev eligibility: allow compose without API
        lastClearTokenId = "local-clear";
      }
    },
  });

  await game.init();

  ui.onStart = async (id) => {
    identity = id;
    playerToken = null;
    if (apiConfigured()) {
      try {
        const res = await postIdentity(id.displayName, id.tag || undefined);
        playerToken = res.playerToken;
      } catch {
        ui.toast("Playing offline — local help queue.");
      }
    }
    ui.show("flight");
    // Begin is a user gesture — unlock + start BGM here
    await game.startRun();
  };

  ui.onAcceptHelp = async () => {
    ui.show("flight");
    let msgs:
      | {
          id: string;
          displayName: string;
          tag?: string;
          body: string;
        }[]
      | undefined;
    if (apiConfigured()) {
      try {
        const res = await getHelpNext(
          playerToken ?? undefined,
          game.helpPullCount(),
        );
        msgs = res.items.map((m) => ({
          id: m.id,
          displayName: m.displayName,
          tag: m.tag,
          body: m.body,
        }));
      } catch {
        ui.toast("Help network unreachable — local archive answering.");
      }
    }
    // Cinema runs inside acceptHelp (world already paused)
    await game.acceptHelp(msgs);
  };

  ui.onRefuseHelp = async () => {
    ui.show("flight");
    // Refuse = start the ritual over from zero (not mid-run continue)
    await game.refuseHelp();
  };

  ui.onCompose = async (body) => {
    const msg: HelperMessage = {
      id: crypto.randomUUID(),
      displayName: identity.displayName,
      tag: identity.tag || undefined,
      body,
    };

    if (
      apiConfigured() &&
      playerToken &&
      lastClearTokenId &&
      lastClearTokenId !== "local-clear"
    ) {
      try {
        await postMessage(playerToken, {
          clearTokenId: lastClearTokenId,
          body,
        });
        lastClearTokenId = null;
        ui.toast("Your message entered the global queue.");
      } catch {
        ui.toast("Could not submit message.");
        return;
      }
    } else {
      localBoard = [msg, ...localBoard];
      ui.toast("Saved locally (API not connected).");
    }
    ui.show("results");
  };

  ui.onShowBoard = async () => {
    if (apiConfigured()) {
      try {
        const res = await getScoreboard(50);
        ui.setBoard(
          res.items.map((m) => ({
            id: m.id,
            displayName: m.displayName,
            tag: m.tag,
            body: m.body,
          })),
        );
        ui.show("board");
        return;
      } catch {
        /* fall through */
      }
    }
    ui.setBoard(localBoard);
    ui.show("board");
  };

  ui.onBackTitle = () => {
    game.stopRun();
    ui.show("title");
  };

  ui.onRetry = async () => {
    ui.show("flight");
    await game.startRun();
  };
}

bootstrap().catch((err) => {
  console.error(err);
  document.body.innerHTML =
    '<p style="color:#e8e6e1;font-family:sans-serif;padding:2rem">Failed to start Ending E. Check console.</p>';
});
